import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@modules/lib/lib'

import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'

export type LayerItemState = 'default' | 'selected' | 'disabled'

type LayerItemProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string
  colorValue?: string
  colorOptions?: ColorOption[]
  showColor?: boolean
  badgeVariant?: 'pill' | 'flat'
  state?: LayerItemState
  /**
   * label の前に表示するドット色。指定時は AI カラーのパレット色バッジではなく
   * ドメイン固有の色分け (例: 積算 v2 の部位色) を表す (issue #2524 で generalize)。
   * 未指定なら従来どおりドットは出さない (AI カラー側の見た目は変わらない)。
   */
  leadingDot?: string
  /**
   * 右側の表示を colorOptions ベースのバッジではなく任意の要素に差し替える。
   * 指定時は showColor / colorValue / colorOptions を無視する
   * (issue #2524: 積算 v2 で面積値テキストを出すための generalize)。
   */
  rightSlot?: React.ReactNode
}

const getColorOption = (value: string | undefined, colorOptions: ColorOption[]) => {
  if (!value) return undefined
  return colorOptions.find(option => option.value === value)
}

/**
 * レイヤー表示用のアイテム
 */
export const LayerItem = forwardRef<HTMLButtonElement, LayerItemProps>(
  (
    {
      label,
      colorValue,
      colorOptions = [],
      showColor = true,
      badgeVariant = 'pill',
      state = 'default',
      leadingDot,
      rightSlot,
      className,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = state === 'disabled' || rest.disabled
    const isSelected = state === 'selected'
    const colorOption = showColor ? getColorOption(colorValue, colorOptions) : undefined

    const colorArea =
      rightSlot !== undefined ? null : showColor ? (
        colorOption ? (
          badgeVariant === 'flat' ? (
            <span className="flex items-center gap-1" data-color-area="true">
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded-[4px] border border-white/50"
                style={{ backgroundColor: colorOption.color }}
              />
              <span className="max-w-[5em] truncate text-xs text-[#18181B]">
                {colorOption.value}
              </span>
            </span>
          ) : (
            <span
              className="flex items-center gap-2 rounded bg-white/70 px-2 py-1"
              data-color-area="true"
            >
              <span
                aria-hidden
                className="h-3.5 w-3.5 rounded border border-white/50"
                style={{ backgroundColor: colorOption.color }}
              />
              <span className="max-w-[5em] truncate text-xs text-[#18181B]">
                {colorOption.value}
              </span>
            </span>
          )
        ) : (
          <span className="text-[10px] text-[#71717A]" data-color-area="true">
            色指定なし
          </span>
        )
      ) : null

    return (
      <button
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border px-4 text-left transition',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400',
          isSelected ? 'border-emerald-400 bg-[#1BD99A]' : 'border-[#E4E4E7] bg-white',
          !isSelected && !isDisabled && 'hover:bg-[#F4F4F5] hover:border-[#E4E4E7]',
          isDisabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        {...rest}
        disabled={isDisabled}
        ref={ref}
        type="button"
      >
        <span className="flex min-w-0 items-center gap-2">
          {leadingDot && (
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: leadingDot }}
            />
          )}
          <span className="min-w-0 truncate text-sm font-medium text-[#18181B]">{label}</span>
        </span>
        {rightSlot ?? colorArea}
      </button>
    )
  },
)

LayerItem.displayName = 'LayerItem'
